import { useState, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Search, Printer, Trash2, Calendar, Download } from "lucide-react";
import { useTalmidim } from "../context/TalmidimContext";
import { usePasses } from "../context/PassesContext";
import { useToast } from "../hooks/use-toast";
import { formatHebrewDateShort, format12Hour } from "../lib/hebrewDate";

export default function History() {
  const { passes, deletePass } = usePasses();
  const { klasn } = useTalmidim();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [klasFilter, setKlasFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const filtered = useMemo(() => {
    return passes.filter((p) => {
      if (search && !p.talmidName.includes(search) && !p.id.toLowerCase().includes(search.toLowerCase())) return false;
      if (klasFilter !== "all" && String(p.klasId) !== klasFilter) return false;
      if (dateFilter && p.date !== dateFilter) return false;
      return true;
    });
  }, [passes, search, klasFilter, dateFilter]);

  const exportCsv = () => {
    const header = ["ID", "Date", "Time", "Student", "Father", "Class", "Reason", "Signed By"];
    const rows = filtered.map((p) => [p.id, p.date, p.time, p.talmidName, p.fatherName, p.klasName, p.reason, p.signedBy]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `late-passes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${filtered.length} records` });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-heb text-3xl font-bold text-slate-900">History</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} of {passes.length} late passes</p>
        </div>
        <Button variant="outline" onClick={exportCsv}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search name or pass ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={klasFilter} onValueChange={setKlasFilter}>
              <SelectTrigger><SelectValue placeholder="All Classes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {klasn.map((k) => <SelectItem key={k.id} value={String(k.id)}><span dir="rtl">{k.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="pl-10 font-en" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Pass #</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Time</th>
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Class</th>
                <th className="px-4 py-3 text-left">Reason</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-16 text-center text-slate-400">No records found</td></tr>
              )}
              {filtered.slice(0, 100).map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-mono text-slate-500">{p.id}</td>
                  <td className="px-4 py-3 text-sm text-slate-700" dir="rtl">{formatHebrewDateShort(p.date)}</td>
                  <td className="px-4 py-3 text-sm font-en text-slate-600">{format12Hour(p.time)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800" dir="rtl">{p.talmidName}</div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="secondary"><span dir="rtl">{p.klasName}</span></Badge></td>
                  <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate" dir="rtl">{p.reason}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <a href={`/print/${p.id}`} target="_blank" rel="noopener noreferrer">
                        <Button size="icon" variant="ghost" title="Reprint"><Printer className="w-4 h-4" /></Button>
                      </a>
                      <Button size="icon" variant="ghost" title="Delete" onClick={() => setConfirmId(p.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 100 && (
          <div className="p-4 text-xs text-slate-500 text-center border-t border-slate-100">Showing 100 of {filtered.length} records</div>
        )}
      </Card>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pass?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => { await deletePass(confirmId); setConfirmId(null); toast({ title: "Pass deleted" }); }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
